import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import { type FC } from 'react';
import { PiArrowSquareOut } from 'react-icons/pi';
import type { ConversationPropertySummaryProps } from 'src/modules/communications/types/conversation-property-summary.type';
import { buildConversationPropertySummaryViewModel } from 'src/modules/communications/utils/conversation-property-summary.util';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { ConversationPropertyPhotoPlaceholder } from '../../../atoms/conversation-property-photo-placeholder';
import { ConversationPropertySummarySkeleton } from './property-summary-skeleton';

/** Renders a stable-size property slot with authorized text and localized states. */
export const ConversationPropertySummary: FC<
  ConversationPropertySummaryProps
> = ({
  propertyId,
  state,
  property,
  imageUrl,
  onImageError,
  onOpenProperty,
  t,
}) => {
  if (state === 'loading') {
    return (
      <div
        className="h-full"
        role="status"
        aria-label={t('communications.conversationCard.property.loading')}
      >
        <ConversationPropertySummarySkeleton />
      </div>
    );
  }

  if (state === 'error' || state === 'unavailable' || !property) {
    const isError = state === 'error';
    const message = t(
      isError
        ? 'communications.conversationCard.property.error'
        : 'communications.conversationCard.property.unavailable'
    );

    return (
      <div
        className="flex min-h-[52px] min-w-0 items-start gap-[7px] border-t border-solid pt-[7px] border-neutral-100"
        role="status"
        aria-label={message}
      >
        <div className="h-[34px] w-[29px] flex-none overflow-hidden rounded-[7px]">
          <ConversationPropertyPhotoPlaceholder
            label={t(
              'communications.conversationCard.property.imageUnavailable'
            )}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <Text
            size="tiny"
            color="secondary"
            className="min-w-0 truncate !whitespace-normal !text-[11px] !leading-[14px]"
            title={message}
          >
            {message}
          </Text>
        </div>
      </div>
    );
  }

  const viewModel = buildConversationPropertySummaryViewModel(
    property,
    propertyId,
    t
  );
  const photoLabel = t('communications.conversationCard.property.imageAlt', {
    name: viewModel.name,
  });
  const openPropertyLabel = `${t('shared.propertySelector.open')}: ${viewModel.code}`;
  const rows =
    viewModel.priceRows.length > 0
      ? viewModel.priceRows
      : [
          {
            label: viewModel.operation,
            accessibleLabel: viewModel.operation,
            value: '',
          },
        ];

  return (
    <div
      className="flex min-h-[52px] min-w-0 items-start gap-[7px] border-t border-solid pt-[7px] border-neutral-100"
      role="group"
      aria-label={viewModel.displayTitle}
    >
      <div className="h-[34px] w-[29px] flex-none overflow-hidden rounded-[7px]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={photoLabel}
            loading="lazy"
            draggable={false}
            width={29}
            height={34}
            onError={onImageError}
            className="h-full w-full flex-none rounded-lg object-cover"
          />
        ) : (
          <ConversationPropertyPhotoPlaceholder
            label={t(
              'communications.conversationCard.property.imageUnavailable'
            )}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <Text
          size="small"
          weight="semibold"
          className="min-w-0 truncate !text-[11px] !leading-[14px]"
          title={viewModel.displayTitle}
        >
          {viewModel.displayTitle}
        </Text>
        <div className="flex min-w-0 flex-col gap-px">
          {rows.map((row, index) => {
            const accessibleRow = row.value
              ? `${row.accessibleLabel}: ${row.value}`
              : row.accessibleLabel;
            return (
              <div
                className="flex min-w-0 items-baseline gap-[5px] leading-[14px]"
                key={`${row.label}-${index}`}
                role="group"
                aria-label={accessibleRow}
                title={accessibleRow}
              >
                <Text
                  size="tiny"
                  color="secondary"
                  className="block min-w-0 max-w-[50%] flex-initial truncate !leading-[14px] text-[11px]"
                  title={row.accessibleLabel}
                >
                  {row.label}
                </Text>
                <Text
                  size="tiny"
                  weight="semibold"
                  className="block min-w-0 flex-initial truncate text-left !leading-[14px] text-[11px] tabular-nums"
                  title={row.value}
                >
                  {row.value}
                </Text>
              </div>
            );
          })}
        </div>
        <Text
          as="span"
          size="tiny"
          weight="regular"
          className="block max-w-full truncate !text-2xs !leading-[14px]"
          title={viewModel.code}
          disableAutoLink
          color="secondary"
        >
          <PropertyCode
            displayCode={property.displayCode}
            fallback={viewModel.codeValue}
          />
        </Text>
      </div>
      {onOpenProperty && (
        <Button
          type="button"
          aria-label={openPropertyLabel}
          title={openPropertyLabel}
          variant="neutral"
          appearance="ghost"
          size="2xs"
          isIconOnly
          className="flex-none self-center focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-current"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenProperty();
          }}
        >
          <PiArrowSquareOut className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
};
